//path: dashboard/src/components/operationsAssociate/AddAssetModal.jsx

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from 'react-toastify';
import Select from "react-select";
import { FiX, FiPackage, FiTag, FiDollarSign, FiUser, FiFileText, FiSave, FiCalendar, FiMapPin, FiSettings, FiTool, FiBriefcase } from "react-icons/fi";

const API_URL = "https://ims.holdthechild.net";

const initialState = {
    name: "",
    category: "Electronics",
    status: "",
    condition: "Good",
    assignedTo: null,
    custodian: null,
    location: "",
    purchaseDate: "",
    quantity: 1,
    unitPrice: "",
    comprehensivePrice: "",
    notes: "",
    projectId: null,
    vendor: null,
    // --- NEW CHANGE: Add budgetCode to initial state ---
    budgetCode: "",
    lastServiced: "",
    expectedEndOfLife: "",
};

export default function AddAssetModal({ closeModal, onAssetAdded }) {
    const [formData, setFormData] = useState(initialState);
    const [loading, setLoading] = useState(false);
    const [staffList, setStaffList] = useState([]);
    const [projectList, setProjectList] = useState([]);
    const [vendorList, setVendorList] = useState([]);
    const modalRef = useRef();

    // Effect to handle clicks outside the modal
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                closeModal();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [closeModal]);

    const staffOptions = staffList.map((staff) => ({
        value: staff._id,
        label: `${staff.firstName} ${staff.lastName}`,
        department: staff.department || "",
    }));

    const projectOptions = projectList.map((project) => ({
        value: project._id,
        label: project.projectTitle,
        assignedProgramManager: project.assignedProgramManager?._id || null,
        assignedProgramManagerName: project.assignedProgramManager ? `${project.assignedProgramManager.firstName} ${project.assignedProgramManager.lastName}` : 'N/A',
        // --- NEW CHANGE: Ensure budgetCode is available in the option object (optional, but good for debugging) ---
        budgetCode: project.budgetCode 
    }));

    const vendorOptions = vendorList.map((vendor) => ({
        value: vendor._id,
        label: vendor.name,
    }));

    useEffect(() => {
        const fetchData = async () => {
            const token = localStorage.getItem("token");
            if (!token) {
                toast.error("Authentication token not found. Please log in.");
                return;
            }
            try {
                const staffRes = await axios.get(`${API_URL}/api/staff`, { headers: { Authorization: `Bearer ${token}` } });
                setStaffList(staffRes.data.data || []);

                const projectRes = await axios.get(`${API_URL}/api/projects`, { headers: { Authorization: `Bearer ${token}` } });
                setProjectList(projectRes.data.data || []);

                const vendorRes = await axios.get(`${API_URL}/api/vendors`, { headers: { Authorization: `Bearer ${token}` } });
                setVendorList(vendorRes.data.data || []);

            } catch (err) {
                console.error("Failed to fetch data:", err);
                toast.error("Failed to load staff or project list. 😢");
            }
        };
        fetchData();
    }, []);

    // --- NEW CHANGE: Updated Effect to capture Budget Code alongside Custodian ---
    useEffect(() => {
        if (formData.projectId) {
            const selectedProject = projectList.find(p => p._id === formData.projectId);
            if (selectedProject) {
                setFormData(prev => ({
                    ...prev,
                    custodian: selectedProject.assignedProgramManager?._id || null,
                    budgetCode: selectedProject.budgetCode || "", // Capture Budget Code
                }));
            } else {
                setFormData(prev => ({ 
                    ...prev, 
                    custodian: null,
                    budgetCode: "" 
                }));
            }
        } else {
            setFormData(prev => ({ 
                ...prev, 
                custodian: null,
                budgetCode: "" 
            }));
        }
    }, [formData.projectId, projectList]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const newForm = { ...prev, [name]: value };

            if (name === "quantity" || name === "unitPrice") {
                const qty = Number(newForm.quantity);
                const unitPrice = Number(newForm.unitPrice);
                if (qty > 0 && unitPrice > 0) {
                    newForm.comprehensivePrice = (qty * unitPrice).toFixed(2);
                } else {
                    newForm.comprehensivePrice = "";
                }
            }
            if (name === "quantity" || name === "comprehensivePrice") {
                const qty = Number(newForm.quantity);
                const comprehensivePrice = Number(newForm.comprehensivePrice);
                if (qty > 0 && comprehensivePrice > 0) {
                    newForm.unitPrice = (comprehensivePrice / qty).toFixed(2);
                } else {
                    newForm.unitPrice = "";
                }
            }

            return newForm;
        });
    };

    const handleStaffSelect = (fieldName, selectedOption) => {
        setFormData(prev => ({
            ...prev,
            [fieldName]: selectedOption ? selectedOption.value : null,
        }));
    };

    const handleSelectChange = (fieldName, selectedOption) => {
        setFormData(prev => ({
            ...prev,
            [fieldName]: selectedOption ? selectedOption.value : null,
        }));
    };

    const handleProjectSelect = (selectedOption) => {
        setFormData(prev => ({
            ...prev,
            projectId: selectedOption ? selectedOption.value : null,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const token = localStorage.getItem("token");
        const submissionData = {
            ...formData,
            purchaseCost: Number(formData.comprehensivePrice),
            unitPrice: Number(formData.unitPrice),
            comprehensivePrice: Number(formData.comprehensivePrice),
            vendor: formData.vendor
            // budgetCode is included here via spread operator
        };
        const quantity = Number(formData.quantity);

        try {
            if (!token) throw new Error("Authentication token not found.");
            if (quantity <= 0 || !submissionData.name) {
                toast.error("Please provide a valid name and quantity.");
                setLoading(false);
                return;
            }

            const assetsToAdd = Array.from({ length: quantity }, () => {
                const { quantity, ...assetData } = submissionData;
                return assetData;
            });

            await axios.post(`${API_URL}/api/assets/bulk`, assetsToAdd, {
                headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
            });

            toast.success(`${quantity} asset(s) added successfully! 🎉`);
            onAssetAdded();
            closeModal();

        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || "An unexpected error occurred.";
            toast.error(`Error: ${errorMessage} 😥`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 backdrop-blur-sm p-4">
            <div 
                ref={modalRef}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl relative transform transition-all duration-300 ease-out flex flex-col max-h-[90vh]"
            >
                {/* Sticky Header with Orange Theme */}
                <div className="bg-gradient-to-r from-orange-300 to-orange-400 rounded-t-2xl p-4 sm:p-6 border-b border-orange-500 sticky top-0 z-10">
                    <div className="flex justify-between items-center">
                        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                                <FiPackage className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
                            </div>
                            <div>
                                <h2 className="text-lg sm:text-xl font-bold text-black">
                                    Add New Asset(s)
                                </h2>
                                <div className="flex items-center mt-1 text-black text-sm">
                                    <FiTag className="w-3 h-3 mr-1" />
                                    <span>Operations Department</span>
                                </div>
                            </div>
                        </div>
                        <button 
                            onClick={closeModal} 
                            className="p-2 rounded-lg transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white"
                        >
                            <FiX size={20} />
                        </button>
                    </div>
                </div>

                {/* Scrollable Form Body */}
                <div className="p-4 sm:p-6 overflow-y-auto flex-grow">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Asset Details Section */}
                        <div className="border border-gray-200 rounded-lg p-4 sm:p-5">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                <FiPackage className="h-5 w-5 mr-2 text-orange-500" />
                                Asset Details
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                                {/* Asset Name */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Asset Name <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FiPackage className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                            className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                            placeholder="e.g., Laptop, Projector"
                                        />
                                    </div>
                                </div>

                                {/* Category */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Category <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FiTag className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <select
                                            name="category"
                                            value={formData.category}
                                            onChange={handleChange}
                                            className="w-full pl-10 pr-10 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 appearance-none"
                                        >
                                            <option value="Electronics">Electronics</option>
                                            <option value="Furniture">Furniture</option>
                                            <option value="Vehicles">Vehicles</option>
                                            <option value="Office Supplies">Office Supplies</option>
                                            <option value="Other">Other</option>
                                        </select>
                                        <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                            <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                                {/* Status */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Status
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FiSettings className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleChange}
                                            className="w-full pl-10 pr-10 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 appearance-none"
                                        >
                                            <option value="">-- Select Status (Optional) --</option>
                                            <option value="Active">Active (In Stock)</option>
                                            <option value="Needs Review">Needs Review</option>
                                            <option value="Under Maintenance">Under Maintenance</option>
                                            <option value="Damaged">Damaged</option>
                                            <option value="Disposed">Disposed</option>
                                        </select>
                                        <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                            <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                    </div>

                                    {/* This message appears automatically if the user assigns someone */}
                                    {(formData.assignedTo || formData.custodian) && (
                                        <div className="mt-2 text-xs text-orange-700 bg-orange-50 p-2 rounded border border-orange-200 flex items-start animate-pulse">
                                            <svg className="w-4 h-4 mr-1.5 mt-0.5 flex-shrink-0 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
                                            </svg>
                                            <span>
                                                <strong>Notice:</strong> Assigning a {formData.assignedTo ? 'User' : 'Custodian'} automatically overrides status to <strong>"Pending Acceptance"</strong>.
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Condition */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Condition <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FiTool className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <select
                                            name="condition"
                                            value={formData.condition}
                                            onChange={handleChange}
                                            className="w-full pl-10 pr-10 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 appearance-none"
                                        >
                                            <option value="New">New</option>
                                            <option value="Good">Good</option>
                                            <option value="Needs Repair">Needs Repair</option>
                                            <option value="Scrap">Scrap</option>
                                        </select>
                                        <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                            <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Acquisition & Quantity Section */}
                        <div className="border border-gray-200 rounded-lg p-4 sm:p-5">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                <FiDollarSign className="h-5 w-5 mr-2 text-orange-500" />
                                Acquisition & Quantity
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                                {/* Quantity */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Quantity <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FiPackage className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="number"
                                            name="quantity"
                                            value={formData.quantity}
                                            onChange={handleChange}
                                            min="1"
                                            required
                                            className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                            placeholder="1"
                                        />
                                    </div>
                                </div>

                                {/* Purchase Date */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Purchase Date
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FiCalendar className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="date"
                                            name="purchaseDate"
                                            value={formData.purchaseDate}
                                            onChange={handleChange}
                                            className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                        />
                                    </div>
                                </div>

                                {/* Unit Price */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Unit Price ($)
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FiDollarSign className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="number"
                                            name="unitPrice"
                                            value={formData.unitPrice}
                                            onChange={handleChange}
                                            min="0"
                                            step="0.01"
                                            className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                            placeholder="e.g., 500"
                                        />
                                    </div>
                                </div>

                                {/* Comprehensive Price */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Comprehensive Price ($)
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FiDollarSign className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="number"
                                            name="comprehensivePrice"
                                            value={formData.comprehensivePrice}
                                            onChange={handleChange}
                                            min="0"
                                            step="0.01"
                                            className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                            placeholder="e.g., 5000"
                                        />
                                    </div>
                                </div>

                                {/* Vendor */}
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Vendor
                                    </label>
                                    <Select
                                        options={vendorOptions}
                                        value={vendorOptions.find(option => option.value === formData.vendor)}
                                        onChange={(selected) => handleSelectChange('vendor', selected)}
                                        placeholder="Select Vendor..."
                                        isClearable
                                        className="react-select-container" 
                                        classNamePrefix="react-select"
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                minHeight: '44px',
                                                borderColor: '#d1d5db',
                                                '&:hover': {
                                                    borderColor: '#f97316',
                                                },
                                                '&:focus-within': {
                                                    borderColor: '#f97316',
                                                    boxShadow: '0 0 0 2px rgba(249, 115, 22, 0.2)',
                                                },
                                            }),
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Maintenance & Lifecycle Section */}
                        <div className="border border-gray-200 rounded-lg p-4 sm:p-5">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                <FiTool className="h-5 w-5 mr-2 text-orange-500" />
                                Maintenance & Lifecycle
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                                {/* Last Serviced */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Last Serviced
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FiCalendar className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="date"
                                            name="lastServiced"
                                            value={formData.lastServiced}
                                            onChange={handleChange}
                                            className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                        />
                                    </div>
                                </div>

                                {/* Expected End of Life */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Expected End of Life
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FiCalendar className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="date"
                                            name="expectedEndOfLife"
                                            value={formData.expectedEndOfLife}
                                            onChange={handleChange}
                                            className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Assignment & Location Section */}
                        <div className="border border-gray-200 rounded-lg p-4 sm:p-5">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                <FiBriefcase className="h-5 w-5 mr-2 text-orange-500" />
                                Assignment & Location
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                                {/* Project Selection */}
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Assign to Project
                                    </label>
                                    <Select
                                        options={projectOptions}
                                        value={projectOptions.find(option => option.value === formData.projectId)}
                                        onChange={handleProjectSelect}
                                        placeholder="Select Project (Optional)..."
                                        isClearable
                                        className="react-select-container" 
                                        classNamePrefix="react-select"
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                minHeight: '44px',
                                                borderColor: '#d1d5db',
                                                '&:hover': {
                                                    borderColor: '#f97316',
                                                },
                                                '&:focus-within': {
                                                    borderColor: '#f97316',
                                                    boxShadow: '0 0 0 2px rgba(249, 115, 22, 0.2)',
                                                },
                                            }),
                                        }}
                                    />
                                </div>

                                {/* --- NEW CHANGE: Budget Code Display --- */}
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Budget Code (Auto-Pulled)
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FiTag className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            name="budgetCode"
                                            value={formData.budgetCode}
                                            readOnly
                                            className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 bg-orange-50 text-orange-800 font-mono focus:outline-none"
                                            placeholder="Select a project to see code"
                                        />
                                    </div>
                                </div>

                                {/* Assigned To */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Assigned To
                                    </label>
                                    <Select
                                        options={staffOptions}
                                        value={staffOptions.find(option => option.value === formData.assignedTo)}
                                        onChange={(selected) => handleStaffSelect('assignedTo', selected)}
                                        placeholder="Select Staff..."
                                        isClearable
                                        className="react-select-container" 
                                        classNamePrefix="react-select"
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                minHeight: '44px',
                                                borderColor: '#d1d5db',
                                                '&:hover': {
                                                    borderColor: '#f97316',
                                                },
                                                '&:focus-within': {
                                                    borderColor: '#f97316',
                                                    boxShadow: '0 0 0 2px rgba(249, 115, 22, 0.2)',
                                                },
                                            }),
                                        }}
                                    />
                                </div>

                                {/* Custodian */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Custodian
                                    </label>
                                    <Select
                                        options={staffOptions}
                                        value={staffOptions.find(option => option.value === formData.custodian)}
                                        onChange={(selected) => handleStaffSelect('custodian', selected)}
                                        placeholder="Select Staff..."
                                        isClearable
                                        isDisabled={!!formData.projectId}
                                        className="react-select-container" 
                                        classNamePrefix="react-select"
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                minHeight: '44px',
                                                borderColor: '#d1d5db',
                                                '&:hover': {
                                                    borderColor: '#f97316',
                                                },
                                                '&:focus-within': {
                                                    borderColor: '#f97316',
                                                    boxShadow: '0 0 0 2px rgba(249, 115, 22, 0.2)',
                                                },
                                            }),
                                        }}
                                    />
                                    {formData.projectId && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            Custodian is auto-assigned from the selected project's Program Manager.
                                        </p>
                                    )}
                                </div>

                                {/* Location */}
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Location <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FiMapPin className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            name="location"
                                            value={formData.location}
                                            onChange={handleChange}
                                            required
                                            className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                            placeholder="e.g., Head Office, Field Site A"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Notes Section */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Notes
                            </label>
                            <div className="relative">
                                <div className="absolute top-3 left-0 pl-3 flex items-start pointer-events-none">
                                    <FiFileText className="h-5 w-5 text-gray-400 mt-0.5" />
                                </div>
                                <textarea
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleChange}
                                    rows="3"
                                    className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                    placeholder="Any relevant information about the asset..."
                                />
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row sm:justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
                            <button
                                type="button"
                                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-all duration-200 order-2 sm:order-1"
                                onClick={closeModal}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-orange-400 to-orange-500 rounded-lg hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-200 flex items-center justify-center space-x-2 order-1 sm:order-2"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <span>Adding...</span>
                                    </>
                                ) : (
                                    <>
                                        <FiSave className="w-4 h-4" />
                                        <span>Add Asset(s)</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}